import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Collapse, Switch, Table, Tag, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faWifi, faBuildingShield, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// ROLE MENU (FlatBuffer Object API — same model RolesPermissionsCards edits)
import {
  getMenuNodeFlags,
  PermissionMode,
  resolveTreeKeyToActionKeys,
  toggleTreeKeysBothModes,
  toggleTreeKeysOneMode,
} from "../../setupWizard/utils/roleMenu";
import type { RoleMenuT } from "../../../flatbuffers/generated/archive/role-menu";
// SIDEBAR TREE (mirrors Layout.tsx's own sidebar hierarchy)
import { SIDEBAR_TREE, SidebarTreeNode } from "./sidebarPermissionTree";
// PERMISSION BUNDLES — the extra permissions one switch turns on with it
import {
  BundleDisablePlan,
  BundleEnablePlan,
  getPermissionBundle,
  PermissionBundle,
  planBundlesDisable,
  planBundlesEnable,
} from "./permissionBundles";
import BundlePermissionsModal from "./BundlePermissionsModal";
// PERMISSION DESCRIPTIONS — same tree_key -> description side-map RolesPermissionsCards reads
import type { PermissionDescriptions } from "./RolesPermissionsCards";
// INTERFACES
import { RootState } from "../../../app/store";

const { Panel } = Collapse;

// Same header color every column title in RolesPermissionsCards uses — kept
// identical rather than shared, this is a one-line JSX color wrapper, not
// permission-mutation logic.
const COLUMN_TITLE_COLOR = "#2968c0";
function renderColumnTitle(label: string) {
  return <span style={{ color: COLUMN_TITLE_COLOR, fontWeight: "bolder" }}>{label}</span>;
}

interface SidebarRow {
  key: string;
  name: string;
  description?: string;
  active: boolean;
  offline: boolean;
  locked: boolean;
  /** The key cannot be granted from this menu (absent, or nothing beneath it to turn on). */
  unavailable: boolean;
}

/**
 * Granted/total for a node — same semantics as RolesPermissionsCards'
 * countGranted: only LEAF nodes (no children) that carry a `key` are
 * counted. A container/group node's own key (when it has one, e.g. the 3
 * section toggles) is never counted on its own — its granted state is
 * always derived from its descendants (see toggleTreeKeysBothModes /
 * applyToPage), so counting it separately would double-count the same
 * fact rather than add information.
 */
function countGranted(node: SidebarTreeNode, menu: RoleMenuT): { granted: number; total: number } {
  if (!node.children || node.children.length === 0) {
    if (!node.key) return { granted: 0, total: 0 };
    const flags = getMenuNodeFlags(menu, node.key);
    return { granted: flags && (flags.active || flags.offline) ? 1 : 0, total: 1 };
  }
  return node.children.reduce(
    (acc, child) => {
      const c = countGranted(child, menu);
      return { granted: acc.granted + c.granted, total: acc.total + c.total };
    },
    { granted: 0, total: 0 },
  );
}

/**
 * A row's React key. `key` is the permission it toggles, and two rows can share
 * one (Drafts and Organizations), so the label is part of the identity.
 */
const sidebarRowKey = (row: SidebarRow): string => `${row.key}::${row.name}`;

/** Green once at least one item under this node is granted, red while it's still zero. */
function renderCountTag(counts: { granted: number; total: number }) {
  return (
    <Tag className="permissionsCard__countTag" color={counts.granted > 0 ? "success" : "error"}>
      {counts.granted}/{counts.total}
    </Tag>
  );
}

function resolveLabel(node: SidebarTreeNode, t: (key: string) => string): string {
  return (node.labelKey && t(node.labelKey)) || node.fallbackLabel;
}

/** One table's rows: every LEAF child of `nodes` (a node with no children of its own). */
function buildRows(
  nodes: SidebarTreeNode[],
  menu: RoleMenuT,
  t: (key: string) => string,
  descriptions: PermissionDescriptions,
): SidebarRow[] {
  return nodes
    .filter((node) => !!node.key)
    .map((node) => {
      const key = node.key as string;
      const flags = getMenuNodeFlags(menu, key);
      return {
        key,
        name: resolveLabel(node, t),
        description: descriptions[key],
        active: !!flags?.active,
        offline: !!flags?.offline,
        locked: !!flags?.locked,
        // A locked key is reported as locked, not as missing.
        unavailable:
          !flags || (!flags.locked && resolveTreeKeyToActionKeys(menu, key).length === 0),
      };
    });
}

/**
 * Roles create/edit/view form's SIDEBAR block: the same tree Layout.tsx
 * renders in the app's left sidebar, laid out with the SAME Collapse +
 * Table pattern (count badge, Description column, merged/split
 * Online-Offline "Permission" column) as RolesPermissionsCards below it —
 * so an operator can show or hide a sidebar tab for this role without
 * hunting for the matching row inside the full Permissions tree (the
 * source of the "how do I hide X from the sidebar" complaints this block
 * exists to fix).
 *
 * This is NOT a separate stored setting — sidebar visibility is already
 * entirely derived from the role's own permission tree_keys (Layout.tsx's
 * `hasPermission`/`isScreenAllowed` calls). Toggling a switch here changes
 * the exact same underlying menu node the Permissions tree below edits, via
 * the same `toggleTreeKeysBothModes`/`toggleTreeKeysOneMode` helpers, so the
 * two stay in sync by construction — never a second source of truth to
 * drift from.
 *
 * A node with a permission bundle (permissionBundles.ts — the Monitoring
 * Dashboard blocks today) is the exception to "same underlying menu node":
 * switching it ON also enables every permission that block's calls need, and
 * switching it OFF also turns off what no other enabled block still needs —
 * each after a confirmation that lists the permissions and lets the operator
 * keep any of them.
 */
function RolesSidebarSection({
  menu,
  onChange,
  readOnly = false,
  descriptions = {},
}: {
  menu: RoleMenuT | null;
  onChange: (next: RoleMenuT) => void;
  readOnly?: boolean;
  descriptions?: PermissionDescriptions;
}) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);

  // Per-table "enable online and offline columns" switch, keyed by the
  // table's own key — same meaning as RolesPermissionsCards' identically
  // named state: missing/false = the default merged "Permission" column;
  // true = the split Online/Offline columns.
  const [combinedColumns, setCombinedColumns] = useState<Record<string, boolean>>({});

  // A bundle switch that has been flipped and is waiting for the operator to
  // confirm (or trim) the permissions it would change. Null = nothing open.
  const [pendingBundle, setPendingBundle] = useState<
    | {
        kind: "enable";
        scopeLabel?: string;
        treeKeys: string[];
        mode: PermissionMode | "both";
        bundles: PermissionBundle[];
        plan: BundleEnablePlan;
      }
    | {
        kind: "disable";
        scopeLabel?: string;
        treeKeys: string[];
        mode: PermissionMode | "both";
        bundles: PermissionBundle[];
        plan: BundleDisablePlan;
      }
    | null
  >(null);

  /** The sidebar row label of a bundle's gate — what the operator knows the block as. */
  const bundleLabel = (bundle: PermissionBundle): string => {
    const find = (nodes: SidebarTreeNode[]): SidebarTreeNode | undefined => {
      for (let i = 0; i < nodes.length; i += 1) {
        if (nodes[i].key === bundle.gateKey) return nodes[i];
        const nested = find(nodes[i].children ?? []);
        if (nested) return nested;
      }
      return undefined;
    };
    const node = find(SIDEBAR_TREE);
    return node ? resolveLabel(node, t) : bundle.gateKey;
  };

  const applyKeys = (keys: string[], mode: PermissionMode | "both", nextValue: boolean) => {
    if (!menu) return;
    onChange(
      mode === "both"
        ? toggleTreeKeysBothModes(menu, keys, nextValue)
        : toggleTreeKeysOneMode(menu, keys, mode, nextValue),
    );
  };

  // Every switch in this block funnels through here. Flipping a bundle's gate
  // opens a confirmation listing what the block would change; once confirmed,
  // the gate(s) and the permissions the operator left on are applied in the
  // SAME mode(s) — one onChange, so neither mode's change overwrites the
  // other. Everything else, and a bundle with nothing to list, applies
  // immediately.
  //
  // `scopeLabel` names what a header switch flips (the table it heads), so the
  // confirmation reads "Enable Reports", not the name of whichever row came first.
  const requestToggle = (
    treeKeys: string[],
    mode: PermissionMode | "both",
    nextValue: boolean,
    scopeLabel?: string,
  ) => {
    if (!menu) return;
    // Two rows can share one gate (Drafts and Organizations): one bundle, once.
    const bundles = Array.from(
      new Set(
        treeKeys
          .map((treeKey) => getPermissionBundle(treeKey))
          .filter((bundle): bundle is PermissionBundle => !!bundle),
      ),
    );
    if (bundles.length === 0) {
      applyKeys(treeKeys, mode, nextValue);
      return;
    }
    if (nextValue) {
      const plan = planBundlesEnable(menu, bundles, mode);
      // Nothing to confirm AND nothing to warn about: apply. A gate that cannot
      // be granted is a warning, so the operator hears about it.
      if (plan.pending.length === 0 && plan.unavailable.length === 0) {
        applyKeys(treeKeys, mode, true);
        return;
      }
      setPendingBundle({ kind: "enable", scopeLabel, treeKeys, mode, bundles, plan });
      return;
    }
    const plan = planBundlesDisable(menu, bundles, mode);
    if (plan.remove.length === 0 && plan.alsoOff.length === 0) {
      applyKeys(treeKeys, mode, false);
      return;
    }
    setPendingBundle({ kind: "disable", scopeLabel, treeKeys, mode, bundles, plan });
  };

  const toggleRowBoth = (treeKey: string, nextValue: boolean) =>
    requestToggle([treeKey], "both", nextValue);

  const toggleKeysMode = (
    treeKeys: string[],
    mode: PermissionMode,
    nextValue: boolean,
    scopeLabel?: string,
  ) => requestToggle(treeKeys, mode, nextValue, scopeLabel);

  const toggleKeysBoth = (treeKeys: string[], nextValue: boolean, scopeLabel?: string) =>
    requestToggle(treeKeys, "both", nextValue, scopeLabel);

  // One row's switch. A key this menu cannot grant is disabled and says why —
  // otherwise its switch flips and nothing happens. (Read-only views render a
  // pruned menu that lacks every un-granted key by design, so no tooltip there.)
  const renderRowSwitch = (
    record: SidebarRow,
    checked: boolean,
    onToggle: (nextValue: boolean) => void,
  ) => {
    const showUnavailable = record.unavailable && !readOnly;
    const rowSwitch = (
      <Switch
        size="small"
        checked={checked}
        disabled={readOnly || record.locked || record.unavailable}
        onChange={onToggle}
      />
    );
    if (!showUnavailable) return rowSwitch;
    return (
      <Tooltip
        title={
          t("organizationManagement.permissionUnavailable") ||
          "Not available in your own permissions, so it cannot be enabled here."
        }
      >
        {/* A disabled switch swallows hover events; the span receives them. */}
        <span className="permissionsCard__unavailableSwitch" style={{ display: "inline-block" }}>
          {rowSwitch}
        </span>
      </Tooltip>
    );
  };

  const renderSwitchCell = (mode: PermissionMode) => (checked: boolean, record: SidebarRow) =>
    renderRowSwitch(record, checked, (nextValue) => toggleKeysMode([record.key], mode, nextValue));

  const renderColumnHeader = (
    label: string,
    mode: PermissionMode,
    rows: SidebarRow[],
    scopeLabel: string,
  ) => {
    const rowKeys = rows.filter((row) => !row.locked).map((row) => row.key);
    const allChecked =
      rows.length > 0 && rows.every((row) => (mode === "online" ? row.active : row.offline));
    return (
      <div
        className="permissionsCard__columnHeader"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <FontAwesomeIcon
          icon={mode === "online" ? faWifi : faBuildingShield}
          style={{ color: COLUMN_TITLE_COLOR }}
        />
        {renderColumnTitle(label)}
        {!readOnly && (
          <Switch
            size="small"
            checked={allChecked}
            onChange={(nextValue) => toggleKeysMode(rowKeys, mode, nextValue, scopeLabel)}
          />
        )}
      </div>
    );
  };

  const renderCombinedColumnHeader = (rows: SidebarRow[], scopeLabel: string) => {
    const rowKeys = rows.filter((row) => !row.locked).map((row) => row.key);
    const allChecked = rows.length > 0 && rows.every((row) => row.active && row.offline);
    return (
      <div
        className="permissionsCard__columnHeader"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <FontAwesomeIcon icon={faWifi} style={{ color: COLUMN_TITLE_COLOR }} />
        <FontAwesomeIcon icon={faBuildingShield} style={{ color: COLUMN_TITLE_COLOR }} />
        {renderColumnTitle(t("organizationManagement.permission") || "Permission")}
        {!readOnly && (
          <Switch
            size="small"
            checked={allChecked}
            onChange={(nextValue) => toggleKeysBoth(rowKeys, nextValue, scopeLabel)}
          />
        )}
      </div>
    );
  };

  const renderCombinedToggle = (tableKey: string) => (
    <span
      className="permissionsCard__combinedToggle"
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      <Switch
        size="small"
        checked={!!combinedColumns[tableKey]}
        onChange={(nextValue) => setCombinedColumns((prev) => ({ ...prev, [tableKey]: nextValue }))}
      />
      <span className="drawer__text permissionsCard__combinedToggleLabel">
        {t("organizationManagement.enableOnlineOfflineColumns") ||
          "Enable online and offline columns"}
      </span>
    </span>
  );

  const buildColumns = (rows: SidebarRow[], tableKey: string, scopeLabel: string) => {
    const showSplitColumns = !!combinedColumns[tableKey];
    const columns: any[] = [
      {
        title: renderColumnTitle(t("organizationManagement.description") || "Description"),
        key: "description",
        width: 80,
        align: "center" as const,
        render: (_: unknown, record: SidebarRow) => (
          <Tooltip
            title={
              record.description ||
              t("organizationManagement.descriptionNotAdded") ||
              "description no added"
            }
          >
            <FontAwesomeIcon icon={faInfoCircle} style={{ color: COLUMN_TITLE_COLOR }} />
          </Tooltip>
        ),
      },
      {
        title: renderColumnTitle(t("organizationManagement.permission") || "Permission"),
        dataIndex: "name",
        key: "name",
        render: (name: string) => <span className="permissionsCard__nameCell">{name}</span>,
      },
    ];

    if (showSplitColumns) {
      columns.push(
        {
          title: renderColumnHeader(
            t("organizationManagement.onlinePermission") || "Online",
            "online",
            rows,
            scopeLabel,
          ),
          dataIndex: "active",
          key: "active",
          width: 250,
          align: "center" as const,
          render: renderSwitchCell("online"),
        },
        {
          title: renderColumnHeader(
            t("organizationManagement.offlinePermission") || "Offline",
            "offline",
            rows,
            scopeLabel,
          ),
          dataIndex: "offline",
          key: "offline",
          width: 250,
          align: "center" as const,
          render: renderSwitchCell("offline"),
        },
      );
    } else {
      columns.push({
        title: renderCombinedColumnHeader(rows, scopeLabel),
        key: "permission",
        width: 250,
        align: "center" as const,
        render: (_: unknown, record: SidebarRow) =>
          renderRowSwitch(record, record.active && record.offline, (nextValue) =>
            toggleRowBoth(record.key, nextValue),
          ),
      });
    }

    return columns;
  };

  if (!menu) {
    return <span className="drawer__text">Select a permission source to edit its menu.</span>;
  }

  const confirmPending = (selectedTreeKeys: string[]) => {
    if (!pendingBundle) return;
    if (pendingBundle.kind === "enable") {
      applyKeys([...pendingBundle.treeKeys, ...selectedTreeKeys], pendingBundle.mode, true);
    } else {
      // The plan's gates cover every block going off (the toggled ones and the
      // enabled blocks that require them), which the row's own key does not.
      applyKeys(
        [...pendingBundle.treeKeys, ...pendingBundle.plan.gates, ...selectedTreeKeys],
        pendingBundle.mode,
        false,
      );
    }
    setPendingBundle(null);
  };

  return (
    <>
      {pendingBundle && (
        <BundlePermissionsModal
          mode={pendingBundle.kind}
          name={
            pendingBundle.bundles.length === 1
              ? bundleLabel(pendingBundle.bundles[0])
              : pendingBundle.scopeLabel || t("sideBar.monitoring") || "Monitoring Dashboard"
          }
          groups={
            pendingBundle.kind === "enable" ? pendingBundle.plan.pending : pendingBundle.plan.remove
          }
          unavailable={pendingBundle.kind === "enable" ? pendingBundle.plan.unavailable : []}
          kept={
            pendingBundle.kind === "disable"
              ? pendingBundle.plan.kept.map(({ treeKey, heldBy }) => ({
                  treeKey,
                  heldBy: heldBy.map(bundleLabel),
                }))
              : []
          }
          alsoOff={
            pendingBundle.kind === "disable" ? pendingBundle.plan.alsoOff.map(bundleLabel) : []
          }
          labelOf={bundleLabel}
          descriptions={descriptions}
          onCancel={() => setPendingBundle(null)}
          onConfirm={confirmPending}
        />
      )}
      <Collapse ghost defaultActiveKey={[]}>
        {SIDEBAR_TREE.map((node) => {
          const label = resolveLabel(node, t);
          const counts = countGranted(node, menu);
          const panelKey = node.key ?? label;

          // Organization Management / Edge Management / Monitoring
          // Dashboard: every child is a leaf (no grouping) — one direct table
          // under the panel, same as a page with actions of its own in
          // RolesPermissionsCards.
          const directNodes = (node.children ?? []).filter(
            (child) => !child.children || child.children.length === 0,
          );
          // Reports: every child is itself a group (General/Faults/Traffic/
          // Car Use/Service Logs) — each gets its own independent block
          // (header + table), same as a page's sub_pages there.
          const blockNodes = (node.children ?? []).filter(
            (child) => child.children && child.children.length > 0,
          );
          const directRows = buildRows(directNodes, menu, t, descriptions);

          return (
            <Panel
              key={panelKey}
              className={`drawer__box__${theme} mb-3`}
              header={
                <div>
                  {renderCountTag(counts)}
                  <FontAwesomeIcon
                    icon={faLock}
                    className="generalStyles__info generalStyles__mrFix"
                  />
                  <span>{label}</span>
                </div>
              }
            >
              {directRows.length > 0 && (
                <div>
                  <div className="permissionsCard__header permissionsCard__block">
                    <div />
                    <div className="permissionsCard__toggles">
                      {renderCombinedToggle(`${panelKey}__direct`)}
                    </div>
                  </div>
                  <Table
                    className="my-custom-table"
                    size="small"
                    pagination={false}
                    rowKey={sidebarRowKey}
                    columns={buildColumns(directRows, `${panelKey}__direct`, label)}
                    dataSource={directRows}
                  />
                </div>
              )}
              {blockNodes.map((block) => {
                const blockLabel = resolveLabel(block, t);
                const blockRows = buildRows(block.children ?? [], menu, t, descriptions);
                if (blockRows.length === 0) return null;
                const blockCounts = countGranted(block, menu);
                const blockKey = `${panelKey}__${blockLabel}`;
                return (
                  <div key={blockKey} className="mb-3">
                    <div className="permissionsCard__header permissionsCard__block">
                      <div className="permissionsCard__title permissionsCard__blockTitle">
                        {renderCountTag(blockCounts)}
                        <Tag color="blue" className="permissionsCard__tableTitleTag">
                          {blockLabel}
                        </Tag>
                      </div>
                      <div className="permissionsCard__toggles">
                        {renderCombinedToggle(blockKey)}
                      </div>
                    </div>
                    <Table
                      size="small"
                      pagination={false}
                      rowKey={sidebarRowKey}
                      columns={buildColumns(blockRows, blockKey, `${label} · ${blockLabel}`)}
                      dataSource={blockRows}
                    />
                  </div>
                );
              })}
            </Panel>
          );
        })}
      </Collapse>
    </>
  );
}

export default RolesSidebarSection;
